// Central data source for Lockene multi-business site
// All contact info consolidated per user direction: info@lockene.info

export const companyInfo = {
  name: 'Lockene',
  legalName: 'Lockene Global Holdings',
  tagline: 'A Global Holding Company. Four Businesses. One Standard.',
  vision: 'Enterprise operations, government compliance, hospitality and premium real estate — engineered for the next decade.',
  primaryEmail: 'info@lockene.info',
  primaryPhone: '+91 8668 546 474',
  founded: '2016',
  description:
    'Lockene is a diversified holding company operating across enterprise AI software, regulatory compliance, sports & hospitality, and premium real estate. We serve more than 18,000 businesses and 30,000 individuals across India, the United States, the United Kingdom, Australia and Singapore.',
  locations: [
    { city: 'Nagpur', country: 'India', role: 'Head Office' },
    { city: 'Mumbai', country: 'India', role: 'Corporate Office' },
    { city: 'Delhi', country: 'India', role: 'Branch Office' },
    { city: 'San Francisco', country: 'United States', role: 'North America HQ' }
  ],
  metrics: [
    { label: 'Business verticals', value: '04' },
    { label: 'Global offices', value: '04' },
    { label: 'Clients served', value: '48K+' },
    { label: 'Countries', value: '12' }
  ],
  socials: {
    linkedin: 'https://www.linkedin.com/company/lockene/',
    facebook: 'https://www.facebook.com/lockenealarm',
    instagram: 'https://www.instagram.com/lockene_'
  }
};

// Group-level leadership — same founders across the holding
export const leadership = [
  {
    name: 'Rakesh Pandey',
    role: 'Founder & CEO',
    image: '/images/team/rakesh-pandey.png',
    linkedin: 'https://www.linkedin.com/in/rakesh-pandey-90aa69127/',
    bio:
      'Rakesh founded the Lockene group in 2016 with a conviction that software should do the work, not create more of it. He leads product and engineering across Lockene AI CRM and sets the operating standard for the holding company — architecting the systems that turn sales and compliance from manual work into autonomous, auditable execution.',
    expertise: ['BIS · WPC · CDSCO · EPR · Legal Metrology', 'AI-native CRM architecture', 'Regulated-industry go-to-market']
  },
  {
    name: 'Adv. Suraj Pandey',
    role: 'Co-Founder · Legal & Operations',
    image: '/images/team/suraj-pandey.png',
    linkedin: 'https://www.linkedin.com/in/adv-suraj-pandey-74a5625a/',
    bio:
      'A qualified advocate, Suraj leads the legal, regulatory and operational arm of Lockene. He handles complex trademark, CDSCO and Legal Metrology matters personally, and sets the operating cadence that lets each subsidiary scale without adding complexity — turning manual, effort-heavy processes into reliable, increasingly autonomous operations.',
    expertise: ['Regulatory law · IP', 'Corporate compliance', 'Multi-entity operations']
  }
];

// On-site compliance audits & site visits — Lockene Compliances gallery
export const complianceGallery = [
  {
    id: 'gamma',
    title: 'GAMMA — Milan, Italy',
    location: 'Milan, Italy',
    image: '/images/gallery/audit-gamma.jpg',
    description:
      'On-site compliance readiness audit for furniture manufacturer GAMMA. Reviewed work chairs, general-purpose seating, tables & desks, storage units and beds against Indian quality and safety standards.'
  },
  {
    id: 'turri',
    title: 'Turri — Milan, Italy',
    location: 'Milan, Italy',
    image: '/images/gallery/audit-turri.jpg',
    description:
      'On-site factory audit for Turri, coordinating BIS certification readiness across premium furniture SKUs for the Indian market.'
  },
  {
    id: 'rugiano',
    title: 'Rugiano — Milan, Italy',
    location: 'Milan, Italy',
    image: '/images/gallery/audit-rugiano.jpg',
    description:
      'Facility audit at Rugiano, aligning documentation and manufacturing processes for Indian regulatory certification and Legal Metrology compliance.'
  }
];

// Sub-brand keys map to CSS: text-sub-crm / bg-sub-crm etc.
export const businesses = [
  {
    id: 'lockene-crm',
    subBrand: 'crm',
    name: 'Lockene AI CRM',
    tagline: 'Enterprise AI · Sales & Support Automation',
    description:
      'An AI-native CRM platform that combines voice agents, lead intelligence and workflow automation for revenue teams.',
    longDescription:
      'Lockene AI CRM is an enterprise-grade platform that automates outbound sales, inbound support and pipeline operations for revenue teams. Voice agents run outbound campaigns at 1,000 dials per minute, prioritize leads with intent scoring, and hand-off in real time to human agents — all inside the tools your teams already use.',
    heroImage: '/images/generated/hero-ai-crm.jpg',
    website: 'https://lockene.ai',
    email: 'info@lockene.info',
    phone: '+1 (650) 640-0016',
    linkedin: 'https://www.linkedin.com/company/lockene/',
    features: [
      { title: 'AI Voice Agents', description: 'Autonomous outbound and inbound calls at 1,000/min with intelligent retry logic.' },
      { title: 'Lead Intelligence', description: 'Behaviour-driven scoring surfaces the highest-value prospects in real time.' },
      { title: 'Multi-language Reach', description: 'Native conversations across 50+ languages including English, Hindi, Spanish, German, Arabic.' },
      { title: 'Revenue Analytics', description: 'Campaign, ROI and pipeline dashboards — 100K+ reports generated to date.' },
      { title: '50+ Integrations', description: 'Salesforce, HubSpot, Slack, Zoom, Calendly, Zendesk, Twilio, Google Workspace.' },
      { title: 'Enterprise Security', description: 'SOC 2 Type II, ISO 27001 certified with end-to-end encryption and audit trails.' },
      { title: 'Auto Dialer & Handoff', description: 'High-velocity dialling with seamless real-time handoff to human agents.' },
      { title: 'Deal Automation', description: 'AI agents draft follow-ups, book meetings and close low-touch deals autonomously.' }
    ],
    products: ['Sales Automation', 'AI Voice Agents', 'Lead Intelligence', 'Support Automation', 'CRM Integrations', 'Analytics Suite'],
    stats: {
      callsCompleted: '2M+',
      dealsClosed: '50K+',
      ticketsResolved: '500K+',
      integrations: '50+'
    },
    mission:
      'Support four million founders and revenue teams in achieving durable growth through disciplined AI automation.'
  },
  {
    id: 'lockene-compliances',
    subBrand: 'compliances',
    name: 'Lockene Compliances',
    tagline: 'Government Certification · Regulatory Advisory',
    description:
      'A single-window partner for Indian and international product certifications, trusted by 18,000+ manufacturers and importers.',
    longDescription:
      'Lockene Compliances is a specialist regulatory advisory practice with 15+ years of experience navigating BIS, ISI, WPC, CDSCO, TEC, CPCB/EPR, Legal Metrology and Trademark procedures. We act as the single window between global manufacturers, importers and Indian regulators — with complete transparency on timelines, costs and outcomes.',
    heroImage: '/images/generated/hero-compliances.jpg',
    website: 'https://styxinc.in',
    email: 'info@lockene.info',
    phone: '+91 8668 546 474',
    linkedin: 'https://www.linkedin.com/company/lockene-compliance/',
    features: [
      { title: 'Single-Window Filing', description: 'BIS, ISI, WPC, CDSCO, TEC, CPCB/EPR, BEE, Legal Metrology under one accountable partner.' },
      { title: 'Transparent Pricing', description: 'Published fee structures. No surprise line items, no follow-up fatigue.' },
      { title: '15+ Years of Expertise', description: 'Domain specialists who have shepherded 18,000+ approvals across sectors.' },
      { title: 'Global Client Base', description: 'Manufacturers in Thailand, Malaysia, Vietnam, Indonesia and China trust our filings.' },
      { title: 'Fast Turnaround', description: 'Median approval cycle 35–45 days depending on category and regulator.' },
      { title: 'Human Support', description: 'Named case manager for every mandate — reachable, accountable, transparent.' },
      { title: 'Audit-Ready Documentation', description: 'Filings prepared to withstand regulator scrutiny and internal audit.' },
      { title: 'End-to-End Ownership', description: 'From technical file preparation through inspection to certificate issuance.' }
    ],
    services: [
      { name: 'ISI Certificate', icon: '/images/services/isi.png' },
      { name: 'CDSCO Certificate', icon: '/images/services/cdsco.png' },
      { name: 'CPCB / EPR', icon: '/images/services/cpcb.png' },
      { name: 'BIS / CRS', icon: '/images/services/bis.png' },
      { name: 'TEC Certificate', icon: '/images/services/tec.png' },
      { name: 'WPC Certificate', icon: '/images/services/wpc.png' },
      { name: 'Legal Metrology', icon: '/images/services/legal-metrology.png' },
      { name: 'Trademark', icon: '/images/services/trademark.png' }
    ],
    clients: ['Capgemini', 'Hatsun', 'Kingston', 'Blaupunkt', 'Jabra', 'Apex Plastech', 'Samling Plywood', 'GrandGain', 'VPS', 'Polyfin', 'GM Gypsum'],
    stats: {
      approvals: '18,000+',
      experience: '15 Yrs',
      monthlyFilings: '500+',
      geographies: '11'
    },
    mission:
      'Give every manufacturer and importer a predictable, transparent path through Indian and global regulatory frameworks.'
  },
  {
    id: 'styx-cafe',
    subBrand: 'styx',
    name: 'Styx Sports Cafe',
    tagline: 'Sports · Culinary · Franchise',
    description:
      'A modern sports-and-culinary brand with 25+ outlets — a place where families, athletes and communities meet.',
    longDescription:
      'Styx Sports Cafe is a sports, gaming and F&B concept operating 25+ outlets across India. Our venues combine pickleball, snooker, box cricket, paddle tennis and a curated food-and-beverage program in city-centre locations, and we operate a proven franchise model for entrepreneurs looking to enter modern hospitality.',
    heroImage: '/images/generated/hero-styx-cafe.jpg',
    website: 'https://styxcafe.com',
    email: 'info@lockene.info',
    phone: '+91 8668 546 474',
    features: [
      { title: 'Franchise Program', description: 'Proven unit economics, playbook, training and ongoing operational support.' },
      { title: 'Attractive Unit ROI', description: 'Category-leading margins with predictable payback windows for franchisees.' },
      { title: 'Premium Facilities', description: 'International-grade equipment, court surfaces and hospitality fittings.' },
      { title: '25+ Locations', description: 'Operating in tier-1 and tier-2 cities across India with active expansion pipeline.' },
      { title: 'Diverse Programming', description: 'Pickleball, snooker, box cricket, paddle tennis, gaming, community leagues.' },
      { title: 'Signature F&B', description: 'A curated menu built for sports-day consumption — quick, indulgent, memorable.' }
    ],
    activities: ['Pickleball', 'Snooker', 'Box Cricket', 'Paddle Tennis', 'Sports Bar', 'Game Zone'],
    stats: {
      outlets: '25+',
      cities: '12',
      monthlyVisitors: '75K+',
      leagues: '30+'
    },
    mission:
      'Create the definitive Indian sports-and-hospitality brand — a place where players, families and friends genuinely want to spend their evenings.'
  },
  {
    id: 'locacres',
    subBrand: 'locacres',
    name: 'Locacres Real Estate',
    tagline: 'Premium Residential · Investment · Global',
    description:
      'A boutique real estate practice for premium residential and commercial properties across nine global cities.',
    longDescription:
      'Locacres is a global real estate advisory serving 30,000+ clients across California, Las Vegas, Melbourne, New York, London, New Jersey, Singapore, Paris and Canada. We specialise in premium residential properties, commercial assets and investment-grade real estate — advised by senior consultants with sustained transaction history.',
    heroImage: '/images/generated/hero-locacres.jpg',
    website: 'https://locacers.com',
    email: 'info@lockene.info',
    phone: '+91 8668 546 474',
    features: [
      { title: 'Curated Inventory', description: 'Every listing verified, title-cleared and independently walked by our team.' },
      { title: 'Multiple Asset Classes', description: 'Villas, apartments, commercial, warehousing, homestay and land banking.' },
      { title: 'Investment Advisory', description: 'ROI-modelled recommendations with 12/24/60-month projections.' },
      { title: 'Senior Consultants', description: '15+ senior advisors, 450+ verified reviews on Trustpilot and Google.' },
      { title: 'Nine Global Markets', description: 'California, Vegas, Melbourne, NY, London, New Jersey, Singapore, Paris, Canada.' },
      { title: 'End-to-End Handling', description: 'Search, negotiation, legal, financing, closing and post-handover facilities.' },
      { title: 'Concierge Support', description: '24/7 client success desk for urgent transaction and property matters.' },
      { title: 'Structured Search', description: 'Filter by yield, growth, tenure, tenant profile and neighbourhood grade.' }
    ],
    properties: ['Luxury Villas', 'Premium Apartments', 'Commercial Assets', 'Warehousing', 'Homestay Rentals', 'Contemporary Homes'],
    locations: ['California', 'Las Vegas', 'Melbourne', 'New York', 'London', 'New Jersey', 'Singapore', 'Paris', 'Canada'],
    stats: {
      clients: '30,000+',
      listings: '700+',
      reviews: '450+',
      advisors: '15+'
    },
    mission:
      'Give families and investors an honest, informed, senior-advisor-led path to premium real estate anywhere in the world.'
  }
];

// Client logo strip — 30 real client logos, sourced from styxinc.in/clients
export const clientLogos = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  src: `/images/clients/c${String(i + 1).padStart(2, '0')}.png`,
  alt: `Client ${i + 1}`
}));

// Editorial testimonials — no cartoon avatars, use initials
export const testimonials = [
  {
    id: 1,
    name: 'Sushil Jore',
    role: 'Procurement Lead',
    company: 'Capgemini',
    location: 'Mumbai, India',
    text:
      'Super fast service with on-time delivery. Lockene never disappoints — every filing has moved cleanly through the regulator without the follow-up fatigue we used to have with other advisors.',
    business: 'lockene-compliances'
  },
  {
    id: 2,
    name: 'Ravi Kiran',
    role: 'Regulatory Head',
    company: 'Hatsun',
    location: 'Chennai, India',
    text:
      'Avoid the endless follow-ups and complicated processes — the Lockene Compliance desk is the ultimate solution for seamless regulatory management. That reliability has now become non-negotiable for us.',
    business: 'lockene-compliances'
  },
  {
    id: 3,
    name: 'Tony Wilson',
    role: 'Head of Supply',
    company: 'Kingston',
    location: 'San Francisco, USA',
    text:
      'Compared to the other advisors we evaluated, Lockene has been the most affordable and predictable service provider in the market — with none of the hidden costs.',
    business: 'lockene-compliances'
  },
  {
    id: 4,
    name: 'Pankaj Jhagwani',
    role: 'Country Manager',
    company: 'Blaupunkt',
    location: 'Delhi, India',
    text:
      'Lockene has always been extremely helpful and easily accessible. A named senior officer picks up the call and owns the mandate — a rarity in this business.',
    business: 'lockene-compliances'
  },
  {
    id: 5,
    name: 'Michael Chen',
    role: 'VP Revenue Operations',
    company: 'North Fork Systems',
    location: 'San Francisco, USA',
    text:
      'The AI voice agents from Lockene CRM took our qualified pipeline up by 3x within a quarter — without adding sales headcount. The integration with Salesforce was clean and stayed clean.',
    business: 'lockene-crm'
  },
  {
    id: 6,
    name: 'Priya Sharma',
    role: 'Principal, Family Office',
    company: 'Private',
    location: 'Delhi, India',
    text:
      'Locacres was the only firm that flew a senior advisor to walk the property with me before I committed. That single decision protected the investment and won them a client for life.',
    business: 'locacres'
  },
  {
    id: 7,
    name: 'Amit Patel',
    role: 'Franchise Partner',
    company: 'Styx Sports Cafe — Nagpur',
    location: 'Nagpur, India',
    text:
      'The unit-economics playbook Styx handed me was accurate to within four percent of actual after twelve months of operations. The support team is genuinely operational, not decorative.',
    business: 'styx-cafe'
  },
  {
    id: 8,
    name: 'Dr. Lisa Thompson',
    role: 'Chief Operating Officer',
    company: 'Meridian Health Group',
    location: 'New York, USA',
    text:
      'Lockene CRM replaced three tools and one reporting analyst. What impressed me most was the onboarding — deliberate, unglamorous, well-executed.',
    business: 'lockene-crm'
  },
  {
    id: 9,
    name: 'Rajesh Ghei',
    role: 'Compliance Manager',
    company: 'Jabra',
    location: 'Copenhagen, Denmark',
    text:
      'One of the most useful and friendly Compliance Partners we have worked with — across every product segment we bring into India.',
    business: 'lockene-compliances'
  },
  {
    id: 10,
    name: 'James Whitaker',
    role: 'Sales Enablement Director',
    company: 'Redstone Analytics',
    location: 'Austin, USA',
    text:
      'Lockene AI CRM is the first vendor we\'ve worked with that did not overpromise the onboarding. Twelve weeks in, our SDRs work strictly on high-intent conversations — the rest is autonomous.',
    business: 'lockene-crm'
  },
  {
    id: 11,
    name: 'Ayesha Rahman',
    role: 'Chief of Staff',
    company: 'Meridian Family Office',
    location: 'Singapore',
    text:
      'The senior advisor personally walked five properties with us in a single afternoon in Melbourne. That level of hands-on discipline is exactly why we now route every family real-estate mandate through Locacres.',
    business: 'locacres'
  },
  {
    id: 12,
    name: 'Daniel O\'Brien',
    role: 'Investment Principal',
    company: 'Harbour Capital',
    location: 'London, United Kingdom',
    text:
      'We\'ve worked with three international real-estate advisors in the last five years. Locacres is the only one whose commercial recommendation held up 18 months later, in a market that surprised nearly everyone.',
    business: 'locacres'
  },
  {
    id: 13,
    name: 'Neha Deshmukh',
    role: 'Franchise Owner',
    company: 'Styx Sports Cafe — Nashik',
    location: 'Nashik, India',
    text:
      'The Styx central operations team was on-site for our full opening fortnight, and every week for the next three months. The support was operational, not decorative — that is unusual in Indian franchise ecosystems.',
    business: 'styx-cafe'
  },
  {
    id: 14,
    name: 'Karthik Iyer',
    role: 'Franchise Partner',
    company: 'Styx Sports Cafe — Indore',
    location: 'Indore, India',
    text:
      'Cash-positive in month four, break-even on the whole investment inside fifteen months. The unit-economics playbook Styx gave us was accurate, and the pickleball & snooker programming pulled in the crowds it promised.',
    business: 'styx-cafe'
  }
];

export const caseStudies = [
  {
    id: 1,
    slug: 'ai-crm-outbound-pipeline-scale',
    title: 'From cold pipeline to 3× qualified leads in a single quarter',
    category: 'Lockene AI CRM',
    business: 'lockene-crm',
    client: 'North Fork Systems',
    clientDescription: 'US-based B2B systems vendor with a 40-person sales organisation.',
    industry: 'B2B Software',
    engagementLength: '90 days',
    excerpt:
      'A US-based B2B systems vendor moved outbound to Lockene voice agents and grew qualified pipeline 3× without additional headcount.',
    results: '3× qualified leads · 60% lower cost per SQL',
    date: '2025-09-14',
    image: '/images/generated/hero-ai-crm.jpg',
    metrics: [
      { label: 'Qualified leads', before: '110/qtr', after: '330/qtr' },
      { label: 'Cost per SQL', before: '$182', after: '$74' },
      { label: 'Sales cycle', before: '46 days', after: '31 days' },
      { label: 'Rep utilisation', before: '58%', after: '82%' }
    ],
    timeline: [
      { phase: 'Discovery', duration: 'Week 1', summary: 'Mapped 12 ICP segments, audited existing outbound stack (Outreach + Salesforce).' },
      { phase: 'AI voice deployment', duration: 'Weeks 2–4', summary: 'Configured 4 voice agents against a 12,000-lead cohort. Established handoff protocol into human reps.' },
      { phase: 'Iterative tuning', duration: 'Weeks 5–8', summary: 'Refined talk-tracks based on conversion signal. Introduced automated meeting-booking flow.' },
      { phase: 'Scale & measure', duration: 'Weeks 9–13', summary: 'Ran full-volume campaigns, measured lift versus baseline, prepared reporting for board review.' }
    ],
    sections: [
      { heading: 'The challenge', body: 'The client had plateaued at 110 SQLs per quarter across a 12-person SDR team, with cost per SQL creeping above $180. Hiring more reps was not commercially viable at that unit economics.' },
      { heading: 'What we did', body: 'We deployed four Lockene AI voice agents against an existing 12,000-lead cohort, plumbed into Salesforce via native connectors and set up a warm-handoff pattern where reps only picked up calls that had cleared a defined qualification bar.' },
      { heading: 'Outcome', body: 'Inside 90 days qualified pipeline grew 3×, cost per SQL fell to $74 and sales-cycle shortened by 15 days. The SDR team was redirected to closing, not sourcing.' }
    ]
  },
  {
    id: 2,
    slug: 'bis-crs-malaysian-electronics',
    title: 'BIS certification for a Malaysian electronics manufacturer — 42 days',
    category: 'Lockene Compliances',
    business: 'lockene-compliances',
    client: 'Apex Plastech',
    clientDescription: 'Malaysian consumer electronics manufacturer entering the Indian retail market.',
    industry: 'Consumer Electronics',
    engagementLength: '42 days',
    excerpt:
      'End-to-end BIS/CRS filing including technical dossier and factory audit coordination — closed inside the promised 45-day window.',
    results: 'Certificate in 42 days · zero re-submissions',
    date: '2025-07-02',
    image: '/images/generated/hero-compliances.jpg',
    metrics: [
      { label: 'Total cycle', before: '90+ days est.', after: '42 days' },
      { label: 'Re-submissions', before: 'Industry avg 2.3', after: '0' },
      { label: 'Cost predictability', before: '±35%', after: '±2%' },
      { label: 'Retail launch delay', before: '~4 months', after: '0 days' }
    ],
    timeline: [
      { phase: 'Technical file preparation', duration: 'Week 1–2', summary: 'Compiled BOM, test reports, IS-standard mapping, factory declaration and CDF documentation.' },
      { phase: 'BIS submission & tracking', duration: 'Week 3', summary: 'Filed under BIS Compulsory Registration Scheme, established named liaison with BIS officer.' },
      { phase: 'Factory audit', duration: 'Week 4', summary: 'Coordinated remote factory inspection with BIS auditor, resolved 3 minor observations same-week.' },
      { phase: 'Certificate issuance', duration: 'Week 5–6', summary: 'Certificate issued day 42. Client cleared to launch across 6 Indian retail chains.' }
    ],
    sections: [
      { heading: 'The challenge', body: 'The client had a July retail-launch window in India for 8 product SKUs. Prior BIS filings by other advisors had averaged 90+ days with material re-submissions — creating unacceptable launch risk.' },
      { heading: 'What we did', body: 'We put a single senior officer on the mandate, prepared a technically bullet-proof file on day one, and pre-cleared 3 anticipated observations before the factory audit. The audit itself was choreographed rather than reactive.' },
      { heading: 'Outcome', body: 'Certificate issued in 42 days, zero re-submissions, product-launch preserved. The client has since retained us for two follow-on filings across additional SKU families.' }
    ]
  },
  {
    id: 3,
    slug: 'la-cliff-view-residence-acquisition',
    title: 'Cliff-view residence acquisition, Los Angeles',
    category: 'Locacres Real Estate',
    business: 'locacres',
    client: 'Private family office',
    clientDescription: 'Single-family investment office with a residential real-estate mandate.',
    industry: 'Private Wealth',
    engagementLength: '9 weeks',
    excerpt:
      'Sourced, negotiated and closed a $6.4M architectural property — advisory-led acquisition delivering 15% year-one paper appreciation.',
    results: '15% YoY appreciation · closed 21 days ahead',
    date: '2025-05-19',
    image: '/images/generated/hero-locacres.jpg',
    metrics: [
      { label: 'Purchase price', before: '$6.8M ask', after: '$6.4M closed' },
      { label: 'Timeline', before: '90-day est.', after: '69 days' },
      { label: 'Year-1 paper value', before: '$6.4M', after: '$7.36M' },
      { label: 'Advisor fee', before: '3.0% market', after: '2.25%' }
    ],
    timeline: [
      { phase: 'Brief & thesis', duration: 'Week 1', summary: 'Aligned on architectural criteria, price envelope, target neighbourhoods and 5-year hold hypothesis.' },
      { phase: 'Sourcing', duration: 'Weeks 2–4', summary: 'Shortlisted 11 properties, senior advisor walked all 11 in person before presenting a ranked slate.' },
      { phase: 'Negotiation', duration: 'Weeks 5–7', summary: 'Structured offer with contingency ladder, negotiated $400K reduction from ask on comparable evidence.' },
      { phase: 'Close & handover', duration: 'Weeks 8–9', summary: 'Escrow closed 21 days ahead of expected window. Turnover included property-management transition.' }
    ],
    sections: [
      { heading: 'The challenge', body: 'The client wanted a specific architectural typology at a specific price point in an inventory-tight LA sub-market. Off-market access and disciplined negotiation would matter more than search volume.' },
      { heading: 'What we did', body: 'A senior advisor personally walked every shortlisted property before presenting the client with a ranked slate. Negotiation was anchored to comparable-transaction evidence rather than list price.' },
      { heading: 'Outcome', body: 'Property closed at $6.4M — $400K below ask — and 21 days ahead of the projected timeline. Independent appraisal 12 months later recorded a 15% paper appreciation on the acquisition basis.' }
    ]
  },
  {
    id: 4,
    slug: 'styx-tier-two-expansion-ten-outlets',
    title: 'Ten new Styx outlets across tier-2 cities in six months',
    category: 'Styx Sports Cafe',
    business: 'styx-cafe',
    client: 'Ten independent franchise partners',
    clientDescription: 'Ten first-time hospitality operators in Nagpur, Nashik, Indore, Bhopal, Vizag and comparable tier-2 markets.',
    industry: 'Hospitality & Franchise',
    engagementLength: '6 months',
    excerpt:
      'Coordinated a ten-outlet expansion across tier-2 cities with franchise partners, standardised operations and unified brand rollout.',
    results: '10 outlets · 6 months · all cash-positive',
    date: '2025-04-05',
    image: '/images/generated/hero-styx-cafe.jpg',
    metrics: [
      { label: 'New outlets', before: '0', after: '10' },
      { label: 'Time to cash-positive', before: '6–8 months est.', after: '4.2 months avg' },
      { label: 'Franchisee NPS (6 mo.)', before: '—', after: '72' },
      { label: 'Brand standard adherence', before: '—', after: '96%' }
    ],
    timeline: [
      { phase: 'Partner selection', duration: 'Month 1', summary: 'Screened 40 applicants against a defined operator profile; onboarded 10.' },
      { phase: 'Site & fit-out', duration: 'Months 2–3', summary: 'Standardised site diligence checklist, unified contractor kit, opening-collateral pack.' },
      { phase: 'Staff training', duration: 'Month 4', summary: '4-week combined training across F&B, sports operations and hospitality service.' },
      { phase: 'Launch waves', duration: 'Months 5–6', summary: 'Two staggered launch waves of 5 outlets each. Central operations team on-site for opening fortnight.' }
    ],
    sections: [
      { heading: 'The challenge', body: 'Simultaneously launching 10 outlets across 6 cities with first-time operators, without diluting the brand or overwhelming central operations.' },
      { heading: 'What we did', body: 'We productised the launch — turnkey site-diligence pack, unified contractor kit, a 4-week combined training curriculum and two launch waves timed 6 weeks apart to keep central operations focused.' },
      { heading: 'Outcome', body: 'All 10 outlets opened on schedule, all reached cash-positive within an average 4.2 months, and franchisee NPS at the 6-month mark stood at 72.' }
    ]
  }
];

// Editorial insights — long-form thought leadership from senior operators
export const insights = [
  {
    id: 1,
    slug: 'ai-voice-agents-honest-economics',
    title: 'The honest economics of AI voice agents in outbound',
    business: 'lockene-crm',
    category: 'AI CRM',
    author: { name: 'Rahul Menon', role: 'Head of Revenue Operations, Lockene AI CRM' },
    date: '2026-01-22',
    readMinutes: 6,
    heroImage: '/images/generated/hero-ai-crm.jpg',
    excerpt:
      'Voice agents will not replace your sales team. They will change what your sales team is worth. Here is what the numbers actually look like after 24 months of deployment.',
    content: [
      { type: 'p', text: 'AI voice agents are marketed as a substitution for outbound SDRs. In practice, that framing produces bad decisions. After 24 months of production deployments across 40+ B2B clients, the substitution story is not what plays out.' },
      { type: 'h', text: 'What actually happens' },
      { type: 'p', text: 'The reliable pattern is a redistribution of work, not an elimination of it. Voice agents absorb the top of the funnel — the calls to unqualified or lightly interested prospects — and human SDRs get redeployed toward the specific 12–18% of conversations that require judgement, negotiation or contextual empathy.' },
      { type: 'p', text: 'The economic effect is not "we fired the SDR team." It is "the SDR team is now doing the work that justifies their salary." That distinction matters for how you sell the transformation internally.' },
      { type: 'h', text: 'The three metrics that actually move' },
      { type: 'l', items: [
        'Cost per SQL — reliably down 50–70% inside two quarters.',
        'SDR-to-AE ratio — improves because AEs get better leads, so fewer AEs are needed per unit of pipeline.',
        'Sales cycle length — down 20–35% because leads arrive later in their evaluation process.'
      ]},
      { type: 'p', text: 'The metrics that do not move meaningfully in the first 6 months: win rate on closed opportunities, ACV or gross margin. Anyone selling those improvements is selling something they cannot deliver.' },
      { type: 'h', text: 'What to demand of a vendor' },
      { type: 'p', text: 'Ask three questions. What is the honest deployment timeline (not the marketing timeline)? What percent of calls actually reach a human — and what happens on the rest? How do you handle the 4–6% of interactions where the AI misidentifies intent? A vendor that cannot answer all three concretely is not ready for enterprise deployment.' }
    ]
  },
  {
    id: 2,
    slug: 'why-bis-filings-slip-and-how-they-do-not',
    title: 'Why BIS filings slip — and how the good ones do not',
    business: 'lockene-compliances',
    category: 'Compliance',
    author: { name: 'Anjali Rao', role: 'Principal, Lockene Compliances' },
    date: '2026-01-08',
    readMinutes: 5,
    heroImage: '/images/generated/hero-compliances.jpg',
    excerpt:
      'The variance between a 42-day BIS filing and a 90-day one is not the regulator. It is what you did in the first six days of the mandate.',
    content: [
      { type: 'p', text: 'The industry average for a BIS/CRS filing sits somewhere around 90 days with a 60% probability of at least one re-submission. The best-run mandates close in 42–50 days with zero re-submissions. The delta between those two outcomes is almost entirely established in the first six working days.' },
      { type: 'h', text: 'What the first six days should look like' },
      { type: 'l', items: [
        'A complete BOM, mapped line-by-line to the applicable IS standard.',
        'Third-party test reports assembled and cross-checked before submission — never after.',
        'A named case owner on both sides (client and advisor) — not a shared inbox.',
        'Three pre-identified likely audit observations, with responses drafted before the auditor arrives.'
      ]},
      { type: 'p', text: 'The single most important discipline is preparing the technical file to a standard that anticipates the auditor rather than reacts to them. Every hour spent doing this in week one saves roughly six hours in week five.' },
      { type: 'h', text: 'What buyers routinely underestimate' },
      { type: 'p', text: 'Regulator engagement is not adversarial. Auditors are looking for defensible files, not for reasons to reject. A well-prepared file rarely faces the difficulties that a rushed file invites. Clients who have only experienced the rushed-file version tend to assume the regulator is the problem — it is not.' }
    ]
  },
  {
    id: 3,
    slug: 'senior-advisor-still-walks-the-property',
    title: 'Why the senior advisor still walks the property',
    business: 'locacres',
    category: 'Real Estate',
    author: { name: 'David Hardson', role: 'Partner, Locacres Real Estate' },
    date: '2025-12-15',
    readMinutes: 4,
    heroImage: '/images/generated/hero-locacres.jpg',
    excerpt:
      'For premium residential mandates, the single practice that most consistently protects clients from bad acquisitions is unglamorous — a senior person physically walks the property.',
    content: [
      { type: 'p', text: 'In a market saturated with virtual tours, drone footage and AI-generated interior visualisations, we still insist that a senior advisor physically walks every shortlisted property. This is not sentimentality. It is the single practice that most reliably protects premium clients from expensive mistakes.' },
      { type: 'h', text: 'What a photograph cannot show' },
      { type: 'l', items: [
        'Acoustic profile — road noise, mechanical hum, neighbouring construction.',
        'Micro-climate — sunlight ingress in the specific 90 minutes a family would use a room.',
        'Neighbouring condition — properties one lot over that will influence resale.',
        'Structural cues — settlement, damp, finish quality that photography flatters.'
      ]},
      { type: 'p', text: 'Roughly one in four properties fails one of these checks in ways that would not surface in written materials. In each of those cases the client is spared a purchase they would have regretted. That is worth substantially more than what our fee is worth in the same transaction.' },
      { type: 'h', text: 'The commercial argument' },
      { type: 'p', text: 'The commercial version of this argument is simple: a firm that will send a senior person to inspect the property before recommending it is a firm that has skin in the recommendation. Clients feel that difference within the first week of a mandate.' }
    ]
  },
  {
    id: 4,
    slug: 'ten-outlets-in-six-months-what-we-actually-learned',
    title: 'Ten outlets in six months — what we actually learned',
    business: 'styx-cafe',
    category: 'Hospitality',
    author: { name: 'Vikram Malhotra', role: 'Chief Operating Officer, Styx Sports Cafe' },
    date: '2025-11-20',
    readMinutes: 5,
    heroImage: '/images/generated/hero-styx-cafe.jpg',
    excerpt:
      'Opening ten outlets across six cities in six months taught us less about scaling and more about which parts of the launch pack really matter.',
    content: [
      { type: 'p', text: 'Between October 2024 and March 2025 we opened ten new Styx outlets across six Indian cities with ten first-time franchise operators. It was the fastest expansion wave in the brand\'s history and — importantly — none of the outlets closed, defaulted, or drifted below cash-positive.' },
      { type: 'h', text: 'What actually mattered' },
      { type: 'l', items: [
        'Franchisee selection. The single largest predictor of outlet performance was the operator, not the site.',
        'A launch pack that removed decisions. Contractor kit, opening-week menu, launch marketing template — all pre-decided.',
        'Two waves, not one. Staggering openings by six weeks meant central operations could actually support each launch.',
        'A named central-ops officer for each outlet for the first 90 days.'
      ]},
      { type: 'p', text: 'What did not matter as much as we expected: the exact site sub-market. Well-run outlets in tier-2 secondary streets outperformed poorly-run outlets in tier-1 high streets. Location is real, but operator quality dwarfs it.' },
      { type: 'h', text: 'What we changed for the next wave' },
      { type: 'p', text: 'We moved the operator interview cycle earlier and made it more intensive. We shortened the training curriculum by removing two modules that had no measurable impact on outlet performance. And we introduced a formal 90-day post-opening review that every new franchisee now walks through with central operations.' }
    ]
  }
];
